import {
  EventData,
  EventTypeEnum,
  Milestone,
  UserEventProgress,
} from '@wwe-portal/entities/rbe.type';
import { EVENT_RBE } from '@wwe-portal/web/utils/config';
import { addDDError } from './datadog';
import { sendCommonTitanEvent } from './common';
import { idbKV } from '../idb';
import { sendGTMEvent } from './gtm-helper';

const getParameterAnalytics = ({
  item,
  typeStatus,
  totalPoints,
  currentMilestone,
}: {
  item: EventData;
  typeStatus: string;
  totalPoints: number | undefined;
  currentMilestone: Milestone | null;
}) => {
  const event_category = item.typeId === 2 ? 'Solo' : 'Global';
  const parameters = {
    type: typeStatus,
    event_id: item.portalEventId.toString(),
    start_time: item.createdAt,
    end_time: item.endDate,
    milestone_id: currentMilestone?.id,
    player_points: totalPoints,
    player_rank: 0,
    event_category,
    milestone_level: currentMilestone?.level ?? null,
  };

  return parameters;
};

const getEventKey = (
  event: Pick<EventData, 'typeId' | 'portalEventId'>,
  userId: string
) => {
  const typeEvent = event.typeId === 2 ? EVENT_RBE.SOLO : EVENT_RBE.GLOBAL;
  return `${userId}_${typeEvent}_${event.portalEventId}`;
};

const getPreviousTotalPoints = async (
  item: Pick<EventData, 'typeId' | 'portalEventId'>,
  userId: string
) => {
  const userKeyEvent = getEventKey(item, userId);
  return idbKV.get<number>(userKeyEvent);
};

const updatePreviousTotalPoints = async (
  item: Pick<EventData, 'typeId' | 'portalEventId'>,
  points: number | undefined,
  userId: string
) => {
  const userKeyEvent = getEventKey(item, userId);
  await idbKV.set(userKeyEvent, points ?? 0);
};

const sendRBEEventStart = ({
  event,
  totalPoints,
  currentMilestone,
}: {
  event: EventData;
  totalPoints: number | undefined;
  currentMilestone: Milestone | null;
}) => {
  const parameter = getParameterAnalytics({
    item: event,
    typeStatus: EVENT_RBE.START,
    totalPoints,
    currentMilestone,
  });
  sendCommonTitanEvent('rbe', {
    ...parameter,
    objective_goal: null,
    objective_name: null,
    objective_type: null,
    objective_points: null,
    milestone_id: null,
    is_milestone: false,
    objective_quantity: 0,
  });
};

const getLastArchivedMilestone = (milestones: Milestone[], point: number) => {
  for (let i = milestones.length - 1; i >= 0; i--) {
    const milestone = milestones[i];
    if (milestone != null && milestone.requirement <= point) {
      return milestone;
    }
  }
  return null;
};

const sendRBEEventProgress = ({
  event,
  previousEventPoints,
  totalPoints,
  currentMilestone,
}: {
  event: EventData;
  previousEventPoints: number;
  totalPoints: number;
  currentMilestone: Milestone | null;
}) => {
  const baseParameter = getParameterAnalytics({
    item: event,
    typeStatus: EVENT_RBE.PROGRESS,
    totalPoints,
    currentMilestone,
  });

  const previousMilestone = getLastArchivedMilestone(
    event.milestones,
    previousEventPoints
  );

  sendCommonTitanEvent('rbe', {
    ...baseParameter,
    objective_goal: 'spend_usd',
    objective_name: 'spend_usd',
    objective_type: 'primary',
    is_milestone: Boolean(
      baseParameter.milestone_id &&
        baseParameter.milestone_id !== previousMilestone?.id
    ),
    objective_quantity: 1,
    objective_points: totalPoints - previousEventPoints,
  });
};

const sendRBEAnalyticEvent = async (
  event: EventData,
  userProgress: UserEventProgress,
  userId: string
) => {
  const previousEventPoints = await getPreviousTotalPoints(event, userId);
  const totalPoints = userProgress.point;
  const currentMilestone = getLastArchivedMilestone(
    event.milestones,
    totalPoints
  );
  await updatePreviousTotalPoints(event, totalPoints, userId);
  if (previousEventPoints == null) {
    sendRBEEventStart({
      event,
      totalPoints,
      currentMilestone,
    });
    return;
  }
  if (previousEventPoints !== totalPoints) {
    sendRBEEventProgress({
      event,
      previousEventPoints,
      totalPoints,
      currentMilestone,
    });
  }
};

export const sendRBEAnalyticsEvents = async ({
  userProgressMap,
  events,
  userId,
}: {
  events: EventData[];
  userProgressMap: Map<number, UserEventProgress>;
  userId: string;
}) => {
  try {
    for (const event of events) {
      const userProgress = userProgressMap.get(event.portalEventId);
      if (!userProgress) continue;
      sendRBEAnalyticEvent(event, userProgress, userId);
    }
  } catch (e) {
    addDDError(e);
  }
};

const gtmEventCategoryMap = {
  [EventTypeEnum.GLOBAL]: 'Global RBE',
  [EventTypeEnum.SOLO]: 'Individual RBE',
};

export const getBaseEventMilestoneParameter = (
  event: EventData,
  milestoneId: string
) => {
  const milestone = event.milestones.find((item) => item.id === milestoneId);
  return {
    name: event.name,
    id: event.portalEventId,
    milestoneId,
    milestoneLevel: milestone?.level,
    milestoneRequirement: milestone?.requirement,
  };
};

const gtmRBEPrefixByType = {
  [EventTypeEnum.GLOBAL]: 'global_rbe',
  [EventTypeEnum.SOLO]: 'individual_rbe',
};

export const sendRBERewardsClaimed = (
  event: EventData,
  milestoneId: string
) => {
  sendGTMEvent({
    category: gtmEventCategoryMap[event.typeId],
    event: `${gtmRBEPrefixByType[event.typeId]}_reward_claimed`,
    data: getBaseEventMilestoneParameter(event, milestoneId),
  });
};

export const sendRBEMilestoneInfoHovered = (
  event: EventData,
  milestoneId: string
) => {
  sendGTMEvent({
    category: gtmEventCategoryMap[event.typeId],
    event: `${gtmRBEPrefixByType[event.typeId]}_milestone_info_hovered`,
    data: getBaseEventMilestoneParameter(event, milestoneId),
  });
};

export const sendRBERewardsCardInfoClick = (
  event: EventData,
  milestoneId: string
) => {
  sendGTMEvent({
    category: gtmEventCategoryMap[event.typeId],
    event: `${gtmRBEPrefixByType[event.typeId]}_reward_info_clicked`,
    data: getBaseEventMilestoneParameter(event, milestoneId),
  });
};
